import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { CurrencyInr, ShoppingCart, Receipt, Clock, TrendUp, TrendDown, X, Phone, WhatsappLogo, CheckCircle, FileText, Funnel, ArrowsClockwise, Plus, PencilSimple, Trash, CaretDown, CaretRight, Package } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SaleEntryForm } from "@/components/forms/SaleEntryForm";
import { ExpenseEntryForm } from "@/components/forms/ExpenseEntryForm";
import { PendingEntryForm } from "@/components/forms/PendingEntryForm";
import { toast } from "sonner";
import { ReportsExportsPanel } from "@/components/reports/ReportsExportsPanel";
import { ExportButtons } from "@/components/reports/ExportButtons";

const VIEW_REPORT = { sales: "sales", expenses: "expense", purchases: "purchase", pending: "pending", profit: "profit-loss" };

const API = `${window.location.origin}/api`;
const fc = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v || 0);
const todayStr = () => new Date().toISOString().split("T")[0];

/* ====== Stat Card (clickable) ====== */
function StatCard({ label, value, icon: Icon, color, bg, border, onClick, sub, testid }) {
  return (
    <button onClick={onClick} className={`text-left p-3 sm:p-4 rounded-xl border-2 ${border} ${bg} hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer w-full`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white flex items-center justify-center shadow-sm`}><Icon size={18} className={color} weight="bold" /></div>
        <span className={`text-[10px] font-bold ${color}`}>VIEW →</span>
      </div>
      <p className={`text-[11px] sm:text-xs font-bold uppercase tracking-wide mt-2 ${color}`}>{label}</p>
      <p className={`text-base sm:text-2xl font-black ${color} font-mono mt-0.5`}>{value}</p>
      {sub && <p className={`text-[10px] font-semibold mt-0.5 ${color} opacity-80`}>{sub}</p>}
    </button>
  );
}

/* ====== Filter Panel ====== */
function FilterPanel({ filters, setFilters, onApply, onReset }) {
  const setQuick = (range) => {
    const t = todayStr();
    let from = t;
    if (range === "week") { const d = new Date(); d.setDate(d.getDate() - 7); from = d.toISOString().split("T")[0]; }
    else if (range === "month") { from = t.slice(0, 8) + "01"; }
    else if (range === "year") { from = t.slice(0, 5) + "01-01"; }
    const next = { ...filters, date_from: from, date_to: t };
    setFilters(next);
    onApply(next);
  };
  return (
    <Card className="p-3 border border-gray-200 bg-white" data-testid="accounts-filter-panel">
      <div className="flex items-center gap-2 mb-2">
        <Funnel size={14} className="text-indigo-600" weight="bold" />
        <span className="text-xs font-bold text-gray-700">Filters</span>
        <div className="flex gap-1 ml-auto flex-wrap">
          {[["today", "Today"], ["week", "Week"], ["month", "Month"], ["year", "Year"]].map(([k, l]) => (
            <Button key={k} size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => setQuick(k)} data-testid={`filter-quick-${k}`}>{l}</Button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <div><Label className="text-[10px] text-gray-500">From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="h-8 text-xs" data-testid="filter-from" /></div>
        <div><Label className="text-[10px] text-gray-500">To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="h-8 text-xs" data-testid="filter-to" /></div>
        <div><Label className="text-[10px] text-gray-500">Customer</Label><Input placeholder="Name..." value={filters.customer_name} onChange={(e) => setFilters((p) => ({ ...p, customer_name: e.target.value }))} className="h-8 text-xs" data-testid="filter-customer" /></div>
        <div><Label className="text-[10px] text-gray-500">Supplier</Label><Input placeholder="Name..." value={filters.supplier} onChange={(e) => setFilters((p) => ({ ...p, supplier: e.target.value }))} className="h-8 text-xs" data-testid="filter-supplier" /></div>
        <div><Label className="text-[10px] text-gray-500">Product</Label><Input placeholder="Name..." value={filters.product_name} onChange={(e) => setFilters((p) => ({ ...p, product_name: e.target.value }))} className="h-8 text-xs" data-testid="filter-product" /></div>
        <div>
          <Label className="text-[10px] text-gray-500">Status</Label>
          <Select value={filters.payment_status} onValueChange={(v) => setFilters((p) => ({ ...p, payment_status: v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-1">
          <Button size="sm" className="h-8 text-xs flex-1 bg-indigo-600 text-white" onClick={() => onApply()} data-testid="apply-filters-btn">Apply</Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={onReset} title="Reset" data-testid="reset-filters-btn"><ArrowsClockwise size={12} /></Button>
        </div>
      </div>
    </Card>
  );
}

/* ====== List Row helpers ====== */
const RowActions = ({ onEdit, onDelete, testid }) => (
  <div className="flex gap-1 ml-2">
    <button onClick={onEdit} className="h-7 w-7 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center" title="Edit" data-testid={`edit-${testid}`}>
      <PencilSimple size={12} weight="bold" />
    </button>
    <button onClick={onDelete} className="h-7 w-7 rounded bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center" title="Delete" data-testid={`delete-${testid}`}>
      <Trash size={12} weight="bold" />
    </button>
  </div>
);

const SaleRow = ({ s, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && items === null && s.bill_number) {
      setLoadingItems(true);
      try {
        const r = await axios.get(`${API}/sales/items`, { params: { bill_number: s.bill_number } });
        setItems(r.data.items || []);
      } catch { setItems([]); }
      finally { setLoadingItems(false); }
    }
  };
  const totalProfit = (items || []).reduce((sum, it) => sum + (it.profit || 0), 0);
  return (
    <div className="border-b border-gray-100" data-testid={`sale-${s.sale_id}`}>
      <div className="flex items-center justify-between p-2.5 text-xs hover:bg-gray-50 cursor-pointer" onClick={toggle} data-testid={`sale-toggle-${s.sale_id}`}>
        <button className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-indigo-600" aria-label="Expand">
          {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </button>
        <div className="min-w-0 flex-1 ml-1">
          <p className="font-bold text-gray-900 truncate">
            {s.bill_number} · {s.customer_name || "Walk-in"}
            {s.gst_type === "with_gst" && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1 rounded">GST {s.gst_percent}%</span>}
            {(s.multi_item_count || 0) > 0 && <span className="ml-1 text-[9px] bg-indigo-100 text-indigo-700 px-1 rounded font-mono">{s.multi_item_count} items</span>}
          </p>
          <p className="text-gray-500">{s.date} · <span className={`px-1 rounded ${s.payment_status === "paid" ? "bg-green-100 text-green-700" : s.payment_status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{s.payment_status}</span></p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-mono font-bold text-green-600">{fc(s.total_bill_amount || s.amount)}</p>
          {s.balance_amount > 0 && <p className="text-[10px] text-red-500">Due: {fc(s.balance_amount)}</p>}
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <RowActions onEdit={onEdit} onDelete={onDelete} testid={`sale-${s.sale_id}`} />
        </div>
      </div>
      {open && (
        <div className="bg-indigo-50/40 border-t border-indigo-100 px-3 py-2 text-[11px]" data-testid={`sale-items-${s.sale_id}`}>
          {loadingItems && <p className="text-gray-400 italic py-2 text-center">Loading items…</p>}
          {!loadingItems && items && items.length === 0 && <p className="text-gray-400 italic py-2 text-center">No line items recorded for this bill.</p>}
          {!loadingItems && items && items.length > 0 && (
            <>
              <div className="grid grid-cols-12 gap-1 px-1.5 pb-1 text-[9px] font-black text-gray-500 uppercase border-b border-indigo-200">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Product</div>
                <div className="col-span-2">Batch</div>
                <div className="col-span-1 text-right">KG</div>
                <div className="col-span-2 text-right">Rate/kg</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-2 text-right">Profit</div>
              </div>
              {items.map((it, i) => (
                <div key={it.entry_id} className="grid grid-cols-12 gap-1 px-1.5 py-1 items-center border-b border-indigo-100 last:border-0" data-testid={`sale-item-${s.sale_id}-${i}`}>
                  <div className="col-span-1 text-gray-500 font-mono">{i + 1}</div>
                  <div className="col-span-3 flex items-center gap-1 text-gray-900 font-semibold truncate">
                    <Package size={10} className="text-indigo-500 flex-shrink-0" weight="bold" />
                    <span className="truncate">{it.product_name}</span>
                  </div>
                  <div className="col-span-2 font-mono text-[10px] text-indigo-600 truncate">{it.batch_code || "FIFO"}</div>
                  <div className="col-span-1 text-right font-mono text-gray-700">{it.kg}</div>
                  <div className="col-span-2 text-right font-mono text-gray-700">{fc(it.kilo_price)}</div>
                  <div className="col-span-1 text-right font-mono font-bold text-green-700">{fc(it.value)}</div>
                  <div className={`col-span-2 text-right font-mono font-bold ${(it.profit || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fc(it.profit || 0)}</div>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-1 px-1.5 pt-1 mt-0.5 border-t border-indigo-300 font-bold">
                <div className="col-span-7 text-gray-700">Bill Total</div>
                <div className="col-span-3 text-right font-mono text-green-700">{fc(s.total_bill_amount || s.amount)}</div>
                <div className={`col-span-2 text-right font-mono ${totalProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fc(totalProfit)}</div>
              </div>
              {s.balance_amount > 0 && (
                <p className="text-[10px] text-red-600 mt-1 font-semibold">Pending balance: {fc(s.balance_amount)}{s.payment_status === "partial" && ` (${fc(s.amount_paid || 0)} paid)`}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
const ExpenseRow = ({ e, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const detailFields = [
    { label: "Description", v: e.description },
    { label: "Category", v: e.category || "general" },
    { label: "Bill #", v: e.bill_number },
    { label: "Customer / Vendor", v: e.customer_name },
    { label: "Payment Method", v: e.payment_method },
    { label: "Paid Amount", v: e.paid_amount != null ? fc(e.paid_amount) : null },
    { label: "Remarks", v: e.remarks || e.notes },
  ].filter((x) => x.v);
  return (
    <div className="border-b border-gray-100" data-testid={`expense-${e.expense_id}`}>
      <div className="flex items-center justify-between p-2.5 text-xs hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`expense-toggle-${e.expense_id}`}>
        <button className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-red-600" aria-label="Expand">
          {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </button>
        <div className="min-w-0 flex-1 ml-1">
          <p className="font-bold text-gray-900 truncate">{e.description || e.bill_number || "Expense"}</p>
          <p className="text-gray-500">{e.date} · {e.category || "general"} {e.bill_number && `· ${e.bill_number}`} {e.customer_name && `· ${e.customer_name}`}</p>
        </div>
        <p className="font-mono font-bold text-red-600 text-right">{fc(e.amount)}</p>
        <div onClick={(ev) => ev.stopPropagation()}>
          <RowActions onEdit={onEdit} onDelete={onDelete} testid={`expense-${e.expense_id}`} />
        </div>
      </div>
      {open && (
        <div className="bg-red-50/40 border-t border-red-100 px-3 py-2 text-[11px]" data-testid={`expense-detail-${e.expense_id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {detailFields.map((f) => (
              <div key={f.label} className="flex justify-between border-b border-red-100/60 py-0.5">
                <span className="text-gray-500 font-semibold">{f.label}</span>
                <span className="text-gray-900 font-mono text-right truncate ml-2">{f.v}</span>
              </div>
            ))}
            <div className="flex justify-between border-b border-red-100/60 py-0.5">
              <span className="text-gray-500 font-semibold">Amount</span>
              <span className="text-red-700 font-mono font-bold text-right ml-2">{fc(e.amount)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const PurchaseRow = ({ p }) => (
  <div className="flex items-center justify-between p-2.5 border-b border-gray-100 text-xs" data-testid={`purchase-${p.entry_id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-gray-900 truncate">{p.product_name} {p.batch_code && <span className="text-[10px] font-mono text-indigo-600 ml-1">[{p.batch_code}]</span>}{p.gst_type === "with_gst" && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1 rounded">GST {p.gst_percent}%</span>}</p>
      <p className="text-gray-500">{p.date} · {p.kg} KG @ {fc(p.kilo_price)}/kg · {p.supplier || "-"}</p>
    </div>
    <p className="font-mono font-bold text-amber-600 text-right">{fc(p.value)}</p>
  </div>
);

/* ====== Pending Row with Actions ====== */
function PendingRow({ p, onMarkPaid, onEdit, onDelete }) {
  const phoneClean = (p.customer_phone || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hi ${p.customer_name || ""}, reminder for invoice ${p.invoice_number || ""} of ₹${p.amount}. Please clear at your earliest. Thanks.`);
  const bucketColor = (b) => b === "0-30" ? "bg-green-100 text-green-700" : b === "31-60" ? "bg-amber-100 text-amber-700" : b === "61-90" ? "bg-orange-100 text-orange-700" : "bg-red-100 text-red-700";
  return (
    <div className="grid grid-cols-12 gap-2 items-center p-2.5 border-b border-gray-100 text-xs hover:bg-gray-50" data-testid={`pending-row-${p.payment_id}`}>
      <div className="col-span-12 sm:col-span-2 text-gray-500 font-mono">{p.date}</div>
      <div className="col-span-12 sm:col-span-3 font-bold text-gray-900 truncate">{p.customer_name || "Unknown"}{p.customer_phone && <span className="text-[10px] text-gray-400 block">{p.customer_phone}</span>}</div>
      <div className="col-span-6 sm:col-span-2 font-mono text-gray-700 truncate">{p.invoice_number || "-"}</div>
      <div className="col-span-6 sm:col-span-1 text-right font-mono font-bold text-red-600">{fc(p.amount)}</div>
      <div className="col-span-6 sm:col-span-1 text-right">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${bucketColor(p.bucket)}`}>{p.due_days}d</span>
      </div>
      <div className="col-span-6 sm:col-span-3 flex justify-end gap-1 flex-wrap">
        {phoneClean && <a href={`tel:${phoneClean}`} className="h-7 w-7 rounded bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100" title="Call" data-testid={`call-${p.payment_id}`}><Phone size={12} weight="bold" /></a>}
        {phoneClean && <a href={`https://wa.me/${phoneClean}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" className="h-7 w-7 rounded bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-100" title="WhatsApp Reminder" data-testid={`wa-${p.payment_id}`}><WhatsappLogo size={12} weight="bold" /></a>}
        <button onClick={() => onMarkPaid(p)} className="h-7 px-2 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[10px] font-bold flex items-center gap-0.5" title="Mark Paid" data-testid={`mark-paid-${p.payment_id}`}><CheckCircle size={11} /> Paid</button>
        <button onClick={onEdit} className="h-7 w-7 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center" title="Edit" data-testid={`edit-pending-${p.payment_id}`}><PencilSimple size={12} weight="bold" /></button>
        <button onClick={onDelete} className="h-7 w-7 rounded bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center" title="Delete" data-testid={`delete-pending-${p.payment_id}`}><Trash size={12} weight="bold" /></button>
      </div>
    </div>
  );
}

/* ====== Main ====== */
export default function AccountsPage() {
  const today = todayStr();
  const monthStart = today.slice(0, 8) + "01";
  const [filters, setFilters] = useState({ date_from: monthStart, date_to: today, customer_name: "", supplier: "", product_name: "", payment_status: "all", team_member: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null); // null | sales | expenses | purchases | pending | profit
  const [aging, setAging] = useState(null);
  const [pendingTrends, setPendingTrends] = useState([]);
  const [showAging, setShowAging] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addTab, setAddTab] = useState("sale");
  const [entryDate, setEntryDate] = useState(today);

  // Trends
  const [trends, setTrends] = useState([]);

  // Quick entry inline forms
  const [qSale, setQSale] = useState({ bill_number: "", amount: "", customer_name: "", customer_contact: "", amount_paid: "", payment_method: "cash", date: today, gst_type: "without_gst", gst_percent: "5", custom_gst: "" });
  const [qExp, setQExp] = useState({ bill_number: "", description: "", amount: "", category: "general", customer_name: "", date: today, payment_method: "cash" });
  const [qPend, setQPend] = useState({ invoice_number: "", customer_name: "", customer_phone: "", amount: "", date: today, due_date: "", remarks: "" });

  // Edit / Delete state
  const [editEntity, setEditEntity] = useState(null); // {type: 'sale'|'expense'|'pending', data: {...}}
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Multi-Product Sale Bill state
  const [showMultiSale, setShowMultiSale] = useState(false);
  const [batches, setBatches] = useState([]);
  const [pendingSales, setPendingSales] = useState([]); // pending/partial sales for Quick Pending picker
  const [msInvoice, setMsInvoice] = useState({ customer_name: "", customer_contact: "", date: today, bill_number: "", payment_method: "cash", amount_paid: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", due_date: "" });
  const [msItems, setMsItems] = useState([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);

  // Edit-Sale line items (lazy loaded when a sale row is opened in Edit dialog)
  const [editItems, setEditItems] = useState([]);
  const [editItemsLoading, setEditItemsLoading] = useState(false);

  useEffect(() => {
    if (editEntity?.type === "sale" && editEntity.data?.bill_number) {
      setEditItemsLoading(true);
      axios.get(`${API}/sales/items`, { params: { bill_number: editEntity.data.bill_number } })
        .then((r) => {
          const items = (r.data.items || []).map((it) => ({
            product_name: it.product_name || "",
            batch_code: it.batch_code || "",
            kg: String(it.kg ?? ""),
            sell_rate: String(it.kilo_price ?? ""),
            notes: it.notes || "",
          }));
          setEditItems(items.length > 0 ? items : [{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
        })
        .catch(() => setEditItems([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]))
        .finally(() => setEditItemsLoading(false));
    } else {
      setEditItems([]);
    }
  }, [editEntity?.type, editEntity?.data?.sale_id, editEntity?.data?.bill_number]);

  const addEditLine = () => setEditItems((p) => [...p, { product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
  const removeEditLine = (idx) => setEditItems((p) => p.length === 1 ? p : p.filter((_, i) => i !== idx));
  const updateEditLine = (idx, field, val) => setEditItems((p) => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const fetchData = useCallback(async (overrideFilters) => {
    setLoading(true);
    try {
      const f = overrideFilters || filters;
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const [dashRes, trRes] = await Promise.all([
        axios.get(`${API}/accounts/dashboard`, { params }),
        axios.get(`${API}/accounts/trends`, { params: { date_from: f.date_from, date_to: f.date_to } }),
      ]);
      setData(dashRes.data);
      setTrends(trRes.data.series || []);
    } catch (err) {
      // Silently swallow auth errors (401) on initial mount; re-fetch will retry
      if (err?.response?.status !== 401) toast.error("Failed to load accounts");
    }
    finally { setLoading(false); }
  }, [filters]);

  const fetchAging = useCallback(async () => {
    try {
      const [a, t, b, ps] = await Promise.all([
        axios.get(`${API}/accounts/pending-aging`),
        axios.get(`${API}/accounts/pending-trends`),
        axios.get(`${API}/inventory/batches`),
        axios.get(`${API}/sales/pending-list`),
      ]);
      setAging(a.data);
      setPendingTrends(t.data);
      setBatches(b.data || []);
      setPendingSales(ps.data || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Failed to load pending trends");
    }
  }, []);

  // Initial load only — subsequent fetches happen via Apply button / Reset / quick presets
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); fetchAging(); }, []);

  const resetFilters = () => {
    const fresh = { date_from: monthStart, date_to: today, customer_name: "", supplier: "", product_name: "", payment_status: "all", team_member: "" };
    setFilters(fresh);
    fetchData(fresh);
  };

  // Multi-Sale helpers
  const addMsLine = () => setMsItems((p) => [...p, { product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
  const removeMsLine = (idx) => setMsItems((p) => p.length === 1 ? p : p.filter((_, i) => i !== idx));
  const updateMsLine = (idx, field, val) => setMsItems((p) => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const msBase = msItems.reduce((sum, it) => sum + ((parseFloat(it.kg) || 0) * (parseFloat(it.sell_rate) || 0)), 0);
  const msGstPct = msInvoice.gst_type === "with_gst" ? (parseFloat(msInvoice.gst_percent === "custom" ? msInvoice.custom_gst : msInvoice.gst_percent) || 0) : 0;
  const msGstAmt = +(msBase * msGstPct / 100).toFixed(2);
  const msGrand = +(msBase + msGstAmt).toFixed(2);
  const msAdvance = parseFloat(msInvoice.amount_paid) || 0;
  const msPendingDue = Math.max(msGrand - msAdvance, 0);

  const submitMultiSale = async () => {
    const validItems = msItems.filter((it) => it.product_name && parseFloat(it.kg) > 0 && parseFloat(it.sell_rate) > 0);
    if (validItems.length === 0) { toast.error("Add at least one valid sale line"); return; }
    if (!msInvoice.customer_name?.trim()) { toast.error("Customer name required"); return; }
    try {
      const payload = {
        customer_name: msInvoice.customer_name,
        customer_contact: msInvoice.customer_contact,
        date: msInvoice.date || today,
        bill_number: msInvoice.bill_number,
        payment_method: msInvoice.payment_method,
        // Always send amount_paid explicitly — backend now treats 0 as pending (not auto-paid).
        // Empty string => null (backend defaults to fully paid for backward compat).
        amount_paid: msInvoice.amount_paid === "" ? null : msAdvance,
        gst_type: msInvoice.gst_type,
        gst_percent: msGstPct,
        due_date: msInvoice.due_date || "",
        items: validItems.map((it) => ({
          product_name: it.product_name,
          batch_code: it.batch_code === "fifo" ? "" : it.batch_code,
          kg: parseFloat(it.kg),
          sell_rate: parseFloat(it.sell_rate),
          notes: it.notes,
        })),
      };
      const res = await axios.post(`${API}/inventory/multi-sale`, payload);
      const pendingMsg = msPendingDue > 0 ? ` · Pending ₹${msPendingDue.toLocaleString("en-IN")}${msInvoice.due_date ? ` due ${msInvoice.due_date}` : ""}` : "";
      toast.success(`Bill ${res.data.bill_number}: ${res.data.items.length} items${pendingMsg}`);
      setShowMultiSale(false);
      setMsInvoice({ customer_name: "", customer_contact: "", date: today, bill_number: "", payment_method: "cash", amount_paid: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", due_date: "" });
      setMsItems([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]);
      fetchData(); fetchAging();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save sale"); }
  };

  const markPaid = async (p) => {
    if (!window.confirm(`Mark ${p.invoice_number} (${fc(p.amount)}) as PAID?`)) return;
    try {
      await axios.patch(`${API}/pending-payments/mark-paid`, null, { params: { date: p.date, invoice_number: p.invoice_number || "" } });
      toast.success("Marked paid");
      fetchData(); fetchAging();
    } catch { toast.error("Mark paid failed"); }
  };

  const submitQuickSale = async () => {
    if (!qSale.bill_number || !qSale.amount) { toast.error("Bill # and amount required"); return; }
    const gstPct = qSale.gst_type === "with_gst" ? (parseFloat(qSale.gst_percent === "custom" ? qSale.custom_gst : qSale.gst_percent) || 0) : 0;
    try {
      await axios.post(`${API}/accounts/quick-sale`, {
        bill_number: qSale.bill_number,
        amount: parseFloat(qSale.amount),
        amount_paid: qSale.amount_paid !== "" ? parseFloat(qSale.amount_paid) : undefined,
        customer_name: qSale.customer_name,
        customer_contact: qSale.customer_contact,
        payment_method: qSale.payment_method,
        date: qSale.date || today,
        gst_type: qSale.gst_type,
        gst_percent: gstPct,
      });
      toast.success("Sale added");
      setQSale({ bill_number: "", amount: "", customer_name: "", customer_contact: "", amount_paid: "", payment_method: "cash", date: today, gst_type: "without_gst", gst_percent: "5", custom_gst: "" });
      fetchData(); fetchAging();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitQuickExpense = async () => {
    if (!qExp.description || !qExp.amount) { toast.error("Description and amount required"); return; }
    try {
      await axios.post(`${API}/accounts/quick-expense`, {
        bill_number: qExp.bill_number,
        description: qExp.description,
        amount: parseFloat(qExp.amount),
        category: qExp.category || "general",
        customer_name: qExp.customer_name,
        payment_method: qExp.payment_method,
        date: qExp.date || today,
      });
      toast.success("Expense added");
      setQExp({ bill_number: "", description: "", amount: "", category: "general", customer_name: "", date: today, payment_method: "cash" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitQuickPending = async () => {
    if (!qPend.customer_name?.trim() || !qPend.amount) { toast.error("Customer and amount required"); return; }
    try {
      await axios.post(`${API}/pending-payments`, {
        date: qPend.date || today,
        invoice_number: qPend.invoice_number || `PND-${Date.now().toString().slice(-6)}`,
        amount: parseFloat(qPend.amount),
        customer_name: qPend.customer_name,
        customer_phone: qPend.customer_phone,
        customer_email: "",
        remarks: qPend.remarks || "Manual pending entry",
        status: "pending",
        due_date: qPend.due_date || "",
      });
      toast.success("Pending added");
      setQPend({ invoice_number: "", customer_name: "", customer_phone: "", amount: "", date: today, due_date: "", remarks: "" });
      fetchData(); fetchAging();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { type, data } = confirmDelete;
    try {
      if (type === "sale") await axios.delete(`${API}/sales/${data.sale_id}`);
      else if (type === "expense") await axios.delete(`${API}/expenses/${data.expense_id}`);
      else if (type === "pending") await axios.delete(`${API}/pending-payments/${data.payment_id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      fetchData(); fetchAging();
    } catch { toast.error("Delete failed"); }
  };

  const handleSaveEdit = async () => {
    if (!editEntity) return;
    const { type, data } = editEntity;
    try {
      if (type === "sale") {
        // If we have items state (lazy-loaded for the sale), use the line-item-aware endpoint
        const validItems = (editItems || []).filter((it) => it.product_name && parseFloat(it.kg) > 0 && parseFloat(it.sell_rate) > 0);
        if (validItems.length > 0) {
          const gstPct = data.gst_type === "with_gst" ? parseFloat(data.gst_percent || 0) : 0;
          await axios.put(`${API}/sales/${data.sale_id}/items`, {
            bill_number: data.bill_number,
            customer_name: data.customer_name,
            customer_contact: data.customer_contact,
            date: data.date,
            payment_method: data.payment_method,
            gst_type: data.gst_type || "without_gst",
            gst_percent: gstPct,
            amount_paid: data.amount_paid === "" || data.amount_paid == null ? null : parseFloat(data.amount_paid),
            due_date: data.due_date || "",
            items: validItems.map((it) => ({
              product_name: it.product_name,
              batch_code: it.batch_code === "fifo" ? "" : it.batch_code,
              kg: parseFloat(it.kg),
              sell_rate: parseFloat(it.sell_rate),
              notes: it.notes,
            })),
          });
        } else {
          // Legacy fallback: aggregate-only edit (sale without line items)
          const base = parseFloat(data.base_amount ?? data.total_bill_amount ?? 0) || 0;
          const gstPct = data.gst_type === "with_gst" ? parseFloat(data.gst_percent || 0) : 0;
          const gstAmt = +(base * gstPct / 100).toFixed(2);
          const grand = +(base + gstAmt).toFixed(2);
          const paid = parseFloat(data.amount_paid) || 0;
          await axios.put(`${API}/sales/${data.sale_id}`, {
            ...data, base_amount: base, gst_amount: gstAmt, grand_total: grand,
            total_bill_amount: grand, amount: grand,
            amount_paid: paid,
            balance_amount: Math.max(grand - paid, 0),
            payment_status: paid >= grand ? "paid" : (paid > 0 ? "partial" : "pending"),
          });
        }
      } else if (type === "expense") {
        await axios.put(`${API}/expenses/${data.expense_id}`, { ...data, amount: parseFloat(data.amount) });
      } else if (type === "pending") {
        await axios.put(`${API}/pending-payments/${data.payment_id}`, { ...data, amount: parseFloat(data.amount) });
      }
      toast.success("Updated");
      setEditEntity(null);
      fetchData(); fetchAging();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const t = data?.totals || {};
  const c = data?.counts || {};

  const maxBucket = useMemo(() => Math.max(1, ...(aging?.buckets || []).map((b) => b.amount)), [aging]);
  const maxTopCust = useMemo(() => Math.max(1, ...(aging?.top_customers || []).map((c) => c.amount)), [aging]);
  const maxTrend = useMemo(() => Math.max(1, ...pendingTrends.map((m) => m.amount)), [pendingTrends]);
  const maxTrendBig = useMemo(() => Math.max(1, ...trends.flatMap((d) => [d.sales, d.expenses, d.purchases])), [trends]);

  return (
    <div className="space-y-4 max-w-7xl" data-testid="accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Accounts Control Center</h1>
          <p className="text-xs font-bold text-indigo-600">{filters.date_from} → {filters.date_to} {loading ? "(loading...)" : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setMsInvoice({ customer_name: "", customer_contact: "", date: today, bill_number: "", payment_method: "cash", amount_paid: "", gst_type: "without_gst", gst_percent: "5", custom_gst: "", due_date: "" }); setMsItems([{ product_name: "", batch_code: "", kg: "", sell_rate: "", notes: "" }]); setShowMultiSale(true); }} data-testid="multi-sale-btn">
            <Plus size={14} className="mr-1" /> Multi-Product Sale Bill
          </Button>
          <Button size="sm" className="h-9 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => { setAddTab("sale"); setShowAdd(true); }} data-testid="add-entry-btn">
            <Plus size={14} className="mr-1" /> Add Entry
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setShowAging(true)} data-testid="open-aging-btn">
            <Clock size={14} className="mr-1" /> Pending Trends & Aging
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel filters={filters} setFilters={setFilters} onApply={fetchData} onReset={resetFilters} />

      {/* 5 Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="account-stats">
        <StatCard label="Sales" value={fc(t.sales)} icon={CurrencyInr} color="text-green-600" bg="bg-green-50" border="border-green-200" sub={`${c.sales || 0} bills · Paid ${fc(t.sales_paid)}`} onClick={() => setView("sales")} testid="card-sales" />
        <StatCard label="Expenses" value={fc(t.expenses)} icon={Receipt} color="text-red-600" bg="bg-red-50" border="border-red-200" sub={`${c.expenses || 0} entries`} onClick={() => setView("expenses")} testid="card-expenses" />
        <StatCard label="Purchases" value={fc(t.purchases)} icon={ShoppingCart} color="text-amber-600" bg="bg-amber-50" border="border-amber-200" sub={`${c.purchases || 0} orders`} onClick={() => setView("purchases")} testid="card-purchases" />
        <StatCard label="Pending Amount" value={fc(t.pending)} icon={Clock} color="text-orange-600" bg="bg-orange-50" border="border-orange-200" sub={`${c.pending || 0} invoices`} onClick={() => setView("pending")} testid="card-pending" />
        <StatCard label="Net Profit" value={fc(t.net_profit)} icon={t.net_profit >= 0 ? TrendUp : TrendDown} color={t.net_profit >= 0 ? "text-indigo-600" : "text-red-600"} bg={t.net_profit >= 0 ? "bg-indigo-50" : "bg-red-50"} border={t.net_profit >= 0 ? "border-indigo-200" : "border-red-200"} sub="click to see breakdown" onClick={() => setView("profit")} testid="card-profit" />
      </div>

      {/* Quick Pending Aging Buckets (always visible) */}
      {aging?.buckets && (
        <Card className="p-3 border border-gray-200 bg-white" data-testid="aging-buckets-strip">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Pending Aging</h3>
            <span className="text-xs text-gray-500 font-mono">Total: <b className="text-red-600">{fc(aging.total)}</b></span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {aging.buckets.map((b) => (
              <div key={b.label} className="text-center p-2 rounded-lg border border-gray-200 bg-gray-50" data-testid={`bucket-${b.label}`}>
                <p className="text-[10px] text-gray-500">{b.label} days</p>
                <p className="text-base font-black font-mono text-red-600">{fc(b.amount)}</p>
                <p className="text-[10px] text-gray-400">{b.count} bills</p>
                <div className="h-1 bg-gray-200 rounded-full mt-1 overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${(b.amount / maxBucket) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quick Entry Strip — Sales + Expenses + Pending */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="quick-entry-strip">
        <Card className="p-3 border-2 border-green-200 bg-green-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-green-600 text-white flex items-center justify-center font-black text-sm">+</div>
            <h3 className="text-sm font-black text-green-700 uppercase tracking-wide" style={{ fontFamily: "'Gupter', serif" }}>Quick Sale</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Input type="date" value={qSale.date} onChange={(e) => setQSale((p) => ({ ...p, date: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-date" />
            <Input placeholder="Bill #" value={qSale.bill_number} onChange={(e) => setQSale((p) => ({ ...p, bill_number: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-bill" />
            <Input placeholder="Customer" value={qSale.customer_name} onChange={(e) => setQSale((p) => ({ ...p, customer_name: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-customer" />
            <Input placeholder="Phone" value={qSale.customer_contact} onChange={(e) => setQSale((p) => ({ ...p, customer_contact: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-phone" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Input type="number" placeholder="Base Amount" value={qSale.amount} onChange={(e) => setQSale((p) => ({ ...p, amount: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-amount" />
            <Input type="number" placeholder="Paid (opt)" value={qSale.amount_paid} onChange={(e) => setQSale((p) => ({ ...p, amount_paid: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qsale-paid" />
            <Select value={qSale.payment_method} onValueChange={(v) => setQSale((p) => ({ ...p, payment_method: v }))}>
              <SelectTrigger className="h-9 text-xs bg-white" data-testid="qsale-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="h-9 text-xs bg-green-600 hover:bg-green-700 text-white font-bold" onClick={submitQuickSale} data-testid="qsale-submit">Save Sale</Button>
          </div>
          {/* GST controls */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <button type="button" onClick={() => setQSale((p) => ({ ...p, gst_type: "without_gst" }))} className={`h-7 px-2 rounded text-[10px] font-bold border ${qSale.gst_type === "without_gst" ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 border-gray-300"}`} data-testid="qsale-gst-without">Not GST Bill</button>
            <button type="button" onClick={() => setQSale((p) => ({ ...p, gst_type: "with_gst" }))} className={`h-7 px-2 rounded text-[10px] font-bold border ${qSale.gst_type === "with_gst" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-300"}`} data-testid="qsale-gst-with">GST Bill</button>
            {qSale.gst_type === "with_gst" && (
              <>
                {["0", "5", "12", "18", "28", "custom"].map((p) => (
                  <button key={p} type="button" onClick={() => setQSale(f => ({ ...f, gst_percent: p }))} className={`h-7 px-2 rounded text-[10px] font-bold border ${qSale.gst_percent === p ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-200"}`} data-testid={`qsale-gst-pct-${p}`}>{p === "custom" ? "Custom" : `${p}%`}</button>
                ))}
                {qSale.gst_percent === "custom" && (
                  <Input type="number" min="0" max="100" placeholder="%" value={qSale.custom_gst} onChange={(e) => setQSale((p) => ({ ...p, custom_gst: e.target.value }))} className="h-7 w-16 text-[10px] bg-white" data-testid="qsale-custom-gst" />
                )}
              </>
            )}
          </div>
          {/* Live preview */}
          {parseFloat(qSale.amount) > 0 && (() => {
            const base = parseFloat(qSale.amount) || 0;
            const pct = qSale.gst_type === "with_gst" ? (parseFloat(qSale.gst_percent === "custom" ? qSale.custom_gst : qSale.gst_percent) || 0) : 0;
            const gstAmt = +(base * pct / 100).toFixed(2);
            const grand = +(base + gstAmt).toFixed(2);
            return (
              <div className="bg-white rounded p-2 text-[11px] flex items-center justify-between border border-green-300" data-testid="qsale-preview">
                <span className="text-gray-500">Base: <b className="text-gray-900 font-mono">{fc(base)}</b></span>
                {qSale.gst_type === "with_gst" && <span className="text-blue-700">GST {pct}%: <b className="font-mono">{fc(gstAmt)}</b></span>}
                <span className="text-green-700 font-black">Grand: <span className="font-mono">{fc(grand)}</span></span>
              </div>
            );
          })()}
        </Card>
        <Card className="p-3 border-2 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-red-600 text-white flex items-center justify-center font-black text-sm">−</div>
            <h3 className="text-sm font-black text-red-700 uppercase tracking-wide" style={{ fontFamily: "'Gupter', serif" }}>Quick Expense</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Input type="date" value={qExp.date} onChange={(e) => setQExp((p) => ({ ...p, date: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qexp-date" />
            <Input placeholder="Bill #" value={qExp.bill_number} onChange={(e) => setQExp((p) => ({ ...p, bill_number: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qexp-bill" />
            <Input placeholder="Customer/Vendor" value={qExp.customer_name} onChange={(e) => setQExp((p) => ({ ...p, customer_name: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qexp-customer" />
            <Select value={qExp.payment_method} onValueChange={(v) => setQExp((p) => ({ ...p, payment_method: v }))}>
              <SelectTrigger className="h-9 text-xs bg-white" data-testid="qexp-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input placeholder="Description" value={qExp.description} onChange={(e) => setQExp((p) => ({ ...p, description: e.target.value }))} className="h-9 text-xs bg-white col-span-2" data-testid="qexp-desc" />
            <Select value={qExp.category} onValueChange={(v) => setQExp((p) => ({ ...p, category: v }))}>
              <SelectTrigger className="h-9 text-xs bg-white" data-testid="qexp-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="food">Food</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="salary">Salary</SelectItem>
                <SelectItem value="rent">Rent</SelectItem>
                <SelectItem value="supplies">Supplies</SelectItem>
                <SelectItem value="misc">Misc</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Amount" value={qExp.amount} onChange={(e) => setQExp((p) => ({ ...p, amount: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qexp-amount" />
          </div>
          <Button size="sm" className="h-9 text-xs bg-red-600 hover:bg-red-700 text-white font-bold w-full mt-2" onClick={submitQuickExpense} data-testid="qexp-submit">Save Expense</Button>
        </Card>
        <Card className="p-3 border-2 border-orange-200 bg-orange-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-orange-500 text-white flex items-center justify-center font-black text-sm">!</div>
            <h3 className="text-sm font-black text-orange-700 uppercase tracking-wide" style={{ fontFamily: "'Gupter', serif" }}>Quick Pending</h3>
            {pendingSales.length > 0 && <span className="ml-auto text-[10px] font-bold text-orange-700 bg-white border border-orange-300 px-2 py-0.5 rounded-full">{pendingSales.length} pending bills available</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Input type="date" value={qPend.date} onChange={(e) => setQPend((p) => ({ ...p, date: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qpend-date" />
            <select
              value={qPend.invoice_number || ""}
              onChange={(e) => {
                const inv = e.target.value;
                if (!inv) { setQPend((p) => ({ ...p, invoice_number: "" })); return; }
                const match = pendingSales.find((s) => s.bill_number === inv);
                if (match) {
                  setQPend((p) => ({
                    ...p,
                    invoice_number: match.bill_number || "",
                    customer_name: match.customer_name || "",
                    customer_phone: match.customer_contact || "",
                    amount: String(match.balance_amount ?? ""),
                    date: match.date || p.date,
                  }));
                } else {
                  setQPend((p) => ({ ...p, invoice_number: inv }));
                }
              }}
              className="h-9 text-xs bg-white border rounded px-2 col-span-1 sm:col-span-2"
              data-testid="qpend-invoice"
            >
              <option value="">— Select pending bill or leave empty —</option>
              {pendingSales.map((s) => (
                <option key={s.sale_id || s.bill_number} value={s.bill_number}>
                  {s.bill_number} · {s.customer_name || "Walk-in"} · ₹{(s.balance_amount ?? 0).toLocaleString("en-IN")} {s.payment_status === "partial" ? "(partial)" : ""} · {s.date}
                </option>
              ))}
            </select>
            <Input placeholder="Customer *" value={qPend.customer_name} onChange={(e) => setQPend((p) => ({ ...p, customer_name: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qpend-customer" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Input placeholder="Phone" value={qPend.customer_phone} onChange={(e) => setQPend((p) => ({ ...p, customer_phone: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qpend-phone" />
            <Input type="number" placeholder="Pending ₹ *" value={qPend.amount} onChange={(e) => setQPend((p) => ({ ...p, amount: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qpend-amount" />
            <Input type="date" value={qPend.due_date} onChange={(e) => setQPend((p) => ({ ...p, due_date: e.target.value }))} placeholder="Due date" className="h-9 text-xs bg-white border-orange-300" data-testid="qpend-due" />
            <Input placeholder="Remarks (opt)" value={qPend.remarks} onChange={(e) => setQPend((p) => ({ ...p, remarks: e.target.value }))} className="h-9 text-xs bg-white" data-testid="qpend-remarks" />
          </div>
          <Button size="sm" className="h-9 text-xs bg-orange-600 hover:bg-orange-700 text-white font-bold w-full" onClick={submitQuickPending} data-testid="qpend-submit">Save Pending</Button>
        </Card>
      </div>

      {/* Trends Chart — Sales / Expenses / Purchases */}
      {trends.length > 0 && (
        <Card className="p-3 border border-gray-200 bg-white" data-testid="trends-chart">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <span className="w-1 h-5 bg-indigo-600 rounded-full"></span>
              Daily Trends ({trends.length} days)
            </h3>
            <div className="flex gap-3 text-[11px] font-bold">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span><span className="text-green-700">Sales</span></span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span><span className="text-red-700">Expenses</span></span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500"></span><span className="text-amber-700">Purchases</span></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex items-end gap-1 h-40 border-b-2 border-gray-200 pb-1 min-w-max">
              {trends.map((d) => (
                <div key={d.date} className="flex flex-col items-center gap-0.5 min-w-[50px] group cursor-help" title={`${d.date}\nSales: ${fc(d.sales)}\nExpenses: ${fc(d.expenses)}\nPurchases: ${fc(d.purchases)}`}>
                  <div className="flex items-end gap-0.5 h-32 w-full justify-center">
                    <div className="w-3 bg-gradient-to-t from-green-600 to-green-300 rounded-t hover:opacity-80 transition-opacity" style={{ height: `${(d.sales / maxTrendBig) * 100}%`, minHeight: d.sales > 0 ? "3px" : "0" }} />
                    <div className="w-3 bg-gradient-to-t from-red-600 to-red-300 rounded-t hover:opacity-80 transition-opacity" style={{ height: `${(d.expenses / maxTrendBig) * 100}%`, minHeight: d.expenses > 0 ? "3px" : "0" }} />
                    <div className="w-3 bg-gradient-to-t from-amber-600 to-amber-300 rounded-t hover:opacity-80 transition-opacity" style={{ height: `${(d.purchases / maxTrendBig) * 100}%`, minHeight: d.purchases > 0 ? "3px" : "0" }} />
                  </div>
                  <span className="text-[9px] font-mono font-bold text-gray-600">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* GST Summary — Sales / Purchases / Liability */}
      {data?.gst && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="gst-summary">
          <Card className="p-2.5 border-2 border-gray-200 bg-gray-50">
            <p className="text-[10px] font-bold text-gray-700 uppercase">Sales Without GST</p>
            <p className="text-base font-black text-gray-700 font-mono">{fc(data.gst.sales_without_gst)}</p>
          </Card>
          <Card className="p-2.5 border-2 border-green-200 bg-green-50">
            <p className="text-[10px] font-bold text-green-700 uppercase">Sales With GST (Base)</p>
            <p className="text-base font-black text-green-700 font-mono">{fc(data.gst.sales_with_gst)}</p>
          </Card>
          <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50">
            <p className="text-[10px] font-bold text-emerald-700 uppercase">Output GST</p>
            <p className="text-base font-black text-emerald-700 font-mono">{fc(data.gst.output_gst)}</p>
            <p className="text-[10px] text-emerald-600">Collected from customers</p>
          </Card>
          <Card className="p-2.5 border-2 border-blue-200 bg-blue-50">
            <p className="text-[10px] font-bold text-blue-700 uppercase">Purchases With GST (Base)</p>
            <p className="text-base font-black text-blue-700 font-mono">{fc(data.gst.purchases_with_gst)}</p>
          </Card>
          <Card className="p-2.5 border-2 border-violet-200 bg-violet-50">
            <p className="text-[10px] font-bold text-violet-700 uppercase">Input GST</p>
            <p className="text-base font-black text-violet-700 font-mono">{fc(data.gst.input_gst)}</p>
            <p className="text-[10px] text-violet-600">Paid to suppliers</p>
          </Card>
          <Card className={`p-2.5 border-2 ${data.gst.liability >= 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}>
            <p className={`text-[10px] font-bold uppercase ${data.gst.liability >= 0 ? "text-red-700" : "text-emerald-700"}`}>GST {data.gst.liability >= 0 ? "Payable" : "Refundable"}</p>
            <p className={`text-base font-black font-mono ${data.gst.liability >= 0 ? "text-red-700" : "text-emerald-700"}`}>{fc(Math.abs(data.gst.liability))}</p>
            <p className={`text-[10px] ${data.gst.liability >= 0 ? "text-red-600" : "text-emerald-600"}`}>Output − Input</p>
          </Card>
        </div>
      )}

      <ReportsExportsPanel defaultFrom={filters.date_from} defaultTo={filters.date_to} />

      {/* ============== Drill-down Dialog ============== */}
      <Dialog open={!!view} onOpenChange={(o) => { if (!o) setView(null); }}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" data-testid={`drill-${view}`}>
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle className="text-lg font-bold capitalize" style={{ fontFamily: "'Gupter', serif" }}>
                {view === "profit" ? "Net Profit Breakdown" : `${view} List`}
              </DialogTitle>
              {view && VIEW_REPORT[view] && (
                <ExportButtons type={VIEW_REPORT[view]} testPrefix={`drill-export-${view}`}
                  params={{ date_from: filters.date_from, date_to: filters.date_to, customer_name: filters.customer_name || undefined, supplier: filters.supplier || undefined }} />
              )}
            </div>
          </DialogHeader>

          {view === "profit" && (
            <div className="space-y-3 py-2" data-testid="profit-breakdown">
              <Card className="p-4 border-2 border-indigo-200 bg-indigo-50">
                <p className="text-xs text-gray-600">Formula</p>
                <p className="text-base font-mono font-bold text-indigo-700">Sales – Purchases – Expenses</p>
              </Card>
              {[
                { l: "Total Sales", v: t.sales || 0, sign: "+", color: "text-green-600", bg: "bg-green-50 border-green-200" },
                { l: "Total Purchases", v: t.purchases || 0, sign: "−", color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
                { l: "Total Expenses", v: t.expenses || 0, sign: "−", color: "text-red-600", bg: "bg-red-50 border-red-200" },
              ].map((r) => (
                <div key={r.l} className={`flex items-center justify-between p-3 rounded-lg border ${r.bg}`}>
                  <div className="flex items-center gap-2"><span className={`text-2xl font-black ${r.color}`}>{r.sign}</span><span className="text-sm font-semibold text-gray-700">{r.l}</span></div>
                  <span className={`text-base font-mono font-bold ${r.color}`}>{fc(r.v)}</span>
                </div>
              ))}
              <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${t.net_profit >= 0 ? "bg-indigo-100 border-indigo-300" : "bg-red-100 border-red-300"}`}>
                <span className="text-sm font-bold text-gray-900">NET PROFIT</span>
                <span className={`text-2xl font-mono font-black ${t.net_profit >= 0 ? "text-indigo-700" : "text-red-700"}`}>{fc(t.net_profit)}</span>
              </div>
            </div>
          )}

          {view === "sales" && (
            <div className="py-2" data-testid="sales-list">
              {(data?.sales || []).length === 0 ? <p className="text-sm text-gray-400 text-center py-8">No sales for this filter</p>
                : data.sales.map((s) => <SaleRow key={s.sale_id} s={s} onEdit={() => setEditEntity({ type: "sale", data: { ...s } })} onDelete={() => setConfirmDelete({ type: "sale", data: s })} />)}
            </div>
          )}
          {view === "expenses" && (
            <div className="py-2" data-testid="expenses-list">
              {(data?.expenses || []).length === 0 ? <p className="text-sm text-gray-400 text-center py-8">No expenses for this filter</p>
                : data.expenses.map((e) => <ExpenseRow key={e.expense_id} e={e} onEdit={() => setEditEntity({ type: "expense", data: { ...e } })} onDelete={() => setConfirmDelete({ type: "expense", data: e })} />)}
            </div>
          )}
          {view === "purchases" && (
            <div className="py-2" data-testid="purchases-list">
              {(data?.purchases || []).length === 0 ? <p className="text-sm text-gray-400 text-center py-8">No purchases for this filter</p>
                : data.purchases.map((p) => <PurchaseRow key={p.entry_id} p={p} />)}
            </div>
          )}
          {view === "pending" && (
            <div className="py-2" data-testid="pending-list">
              <div className="hidden sm:grid grid-cols-12 gap-2 p-2 border-b-2 border-gray-200 text-[10px] font-bold text-gray-500 uppercase">
                <div className="col-span-2">Date</div><div className="col-span-3">Customer</div><div className="col-span-2">Invoice</div><div className="col-span-2 text-right">Pending</div><div className="col-span-1 text-right">Days</div><div className="col-span-2 text-right">Actions</div>
              </div>
              {(aging?.items || []).filter((p) => !filters.customer_name || (p.customer_name || "").toLowerCase().includes(filters.customer_name.toLowerCase())).length === 0 ? <p className="text-sm text-gray-400 text-center py-8">No pending payments</p>
                : (aging?.items || []).filter((p) => !filters.customer_name || (p.customer_name || "").toLowerCase().includes(filters.customer_name.toLowerCase())).map((p) => <PendingRow key={p.payment_id} p={p} onMarkPaid={markPaid} onEdit={() => setEditEntity({ type: "pending", data: { ...p } })} onDelete={() => setConfirmDelete({ type: "pending", data: p })} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============== Pending Trends & Aging Dialog ============== */}
      <Dialog open={showAging} onOpenChange={setShowAging}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" data-testid="aging-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Pending Trends & Aging</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Aging buckets */}
            <div>
              <h3 className="text-xs font-bold text-gray-700 mb-2">Aging Buckets</h3>
              <div className="space-y-2">
                {(aging?.buckets || []).map((b) => (
                  <div key={b.label} className="flex items-center gap-3" data-testid={`aging-row-${b.label}`}>
                    <span className="text-xs font-bold text-gray-700 w-16">{b.label}d</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className={`h-full ${b.label === "0-30" ? "bg-green-500" : b.label === "31-60" ? "bg-amber-500" : b.label === "61-90" ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${(b.amount / maxBucket) * 100}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-gray-800">{b.count} bills · {fc(b.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Top pending customers */}
            <div>
              <h3 className="text-xs font-bold text-gray-700 mb-2">Top Pending Customers</h3>
              <div className="space-y-1.5">
                {(aging?.top_customers || []).length === 0 ? <p className="text-xs text-gray-400">No data</p> :
                  aging.top_customers.map((c, i) => (
                    <div key={c.customer_name} className="flex items-center gap-2 text-xs" data-testid={`top-cust-${i}`}>
                      <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-black text-[10px]">{i + 1}</span>
                      <span className="font-semibold text-gray-900 w-32 truncate">{c.customer_name}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                        <div className="h-full bg-red-400" style={{ width: `${(c.amount / maxTopCust) * 100}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold">{c.count} bills</span>
                      </div>
                      <span className="font-mono font-bold text-red-600 w-20 text-right">{fc(c.amount)}</span>
                    </div>
                  ))}
              </div>
            </div>
            {/* Monthly trend */}
            <div>
              <h3 className="text-xs font-bold text-gray-700 mb-2">Pending by Month</h3>
              {pendingTrends.length === 0 ? <p className="text-xs text-gray-400">No data</p> : (
                <div className="flex items-end gap-1 h-32 border-b border-gray-200 pb-1" data-testid="trend-chart">
                  {pendingTrends.map((m) => (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-[24px]">
                      <span className="text-[9px] text-gray-500 font-mono">{fc(m.amount).replace("₹", "")}</span>
                      <div className="w-full bg-gradient-to-t from-red-500 to-orange-300 rounded-t" style={{ height: `${(m.amount / maxTrend) * 100}%`, minHeight: "4px" }} />
                      <span className="text-[9px] text-gray-500">{m.month.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== Add Entry Dialog ============== */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="add-entry-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Add New Entry</DialogTitle>
          </DialogHeader>
          <div className="mb-3">
            <Label className="text-xs font-semibold text-gray-600">Entry Date</Label>
            <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="h-9 text-sm" data-testid="entry-date" />
          </div>
          <Tabs value={addTab} onValueChange={setAddTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="sale" data-testid="tab-sale">Sale</TabsTrigger>
              <TabsTrigger value="expense" data-testid="tab-expense">Expense</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            </TabsList>
            <TabsContent value="sale" className="mt-3">
              <SaleEntryForm selectedDate={entryDate} onEntryAdded={() => { setShowAdd(false); fetchData(); fetchAging(); toast.success("Sale added"); }} />
            </TabsContent>
            <TabsContent value="expense" className="mt-3">
              <ExpenseEntryForm selectedDate={entryDate} onEntryAdded={() => { setShowAdd(false); fetchData(); toast.success("Expense added"); }} />
            </TabsContent>
            <TabsContent value="pending" className="mt-3">
              <PendingEntryForm selectedDate={entryDate} onEntryAdded={() => { setShowAdd(false); fetchData(); fetchAging(); toast.success("Pending added"); }} />
            </TabsContent>
          </Tabs>
          <p className="text-[10px] text-gray-400 text-center mt-3">For purchase entries, use the <b>Purchase Dashboard</b> menu (left sidebar)</p>
        </DialogContent>
      </Dialog>

      {/* ============== Edit Dialog ============== */}
      <Dialog open={!!editEntity} onOpenChange={(o) => { if (!o) setEditEntity(null); }}>
        <DialogContent className={`max-h-[92vh] overflow-y-auto ${editEntity?.type === "sale" ? "sm:max-w-[920px]" : "sm:max-w-[520px]"}`} data-testid="edit-entity-dialog">
          {editEntity && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold capitalize" style={{ fontFamily: "'Gupter', serif" }}>Edit {editEntity.type}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {editEntity.type === "sale" && (
                  <>
                    <div><Label className="text-xs font-bold">Bill Number</Label><Input value={editEntity.data.bill_number || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, bill_number: e.target.value } }))} className="h-9 text-sm" data-testid="edit-sale-bill" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={editEntity.data.date || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, date: e.target.value } }))} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs font-bold">Customer</Label><Input value={editEntity.data.customer_name || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, customer_name: e.target.value } }))} className="h-9 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Phone</Label><Input value={editEntity.data.customer_contact || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, customer_contact: e.target.value } }))} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs font-bold">Payment Method</Label>
                        <Select value={editEntity.data.payment_method || "cash"} onValueChange={(v) => setEditEntity((p) => ({ ...p, data: { ...p.data, payment_method: v } }))}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* ===== Line Items Editor ===== */}
                    <div className="border-2 border-indigo-200 rounded-lg overflow-hidden bg-indigo-50/30">
                      <div className="bg-indigo-100 p-2 flex items-center justify-between">
                        <h4 className="text-xs font-black text-indigo-800 uppercase flex items-center gap-1">
                          <Package size={12} weight="bold" /> Line Items {editItems.length > 0 ? `(${editItems.length})` : ""}
                        </h4>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] bg-white" onClick={addEditLine} data-testid="edit-add-line">
                          <Plus size={12} className="mr-0.5" /> Add Item
                        </Button>
                      </div>
                      <div className="divide-y divide-indigo-100 max-h-[280px] overflow-y-auto">
                        {editItemsLoading ? (
                          <p className="text-xs text-gray-400 italic text-center py-4">Loading items…</p>
                        ) : editItems.length === 0 ? (
                          <p className="text-xs text-gray-400 italic text-center py-4">No items yet — click <b>Add Item</b> to start</p>
                        ) : (
                          editItems.map((it, idx) => {
                            const kgN = parseFloat(it.kg) || 0;
                            const rateN = parseFloat(it.sell_rate) || 0;
                            const lineBase = +(kgN * rateN).toFixed(2);
                            const availBatches = batches.filter(b => b.product_name === it.product_name && b.kg_remaining > 0);
                            const productOptions = [...new Set(batches.filter(b => b.kg_remaining > 0).map(b => b.product_name))];
                            // include current batch even if depleted (so user can keep it)
                            const currentBatch = batches.find(b => b.batch_code === it.batch_code && b.product_name === it.product_name);
                            const batchOptions = currentBatch && !availBatches.includes(currentBatch) ? [currentBatch, ...availBatches] : availBatches;
                            return (
                              <div key={idx} className="p-2 bg-white" data-testid={`edit-line-${idx}`}>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black flex items-center justify-center">{idx + 1}</span>
                                  <select value={it.product_name} onChange={(e) => { updateEditLine(idx, "product_name", e.target.value); updateEditLine(idx, "batch_code", ""); }} className="h-8 text-xs border rounded flex-1 bg-white px-2" data-testid={`edit-prod-${idx}`}>
                                    <option value="">Select Product</option>
                                    {it.product_name && !productOptions.includes(it.product_name) && <option value={it.product_name}>{it.product_name}</option>}
                                    {productOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                                  </select>
                                  <select value={it.batch_code} onChange={(e) => updateEditLine(idx, "batch_code", e.target.value)} className="h-8 text-xs border rounded w-44 bg-white px-2" data-testid={`edit-batch-${idx}`}>
                                    <option value="">Auto FIFO</option>
                                    {batchOptions.map((b) => <option key={b.batch_code} value={b.batch_code}>{b.batch_code} · {b.kg_remaining}kg @ ₹{b.kilo_price}</option>)}
                                  </select>
                                  <button onClick={() => removeEditLine(idx)} disabled={editItems.length === 1} className="h-7 w-7 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-30 flex items-center justify-center" data-testid={`edit-remove-${idx}`}>
                                    <Trash size={12} />
                                  </button>
                                </div>
                                <div className="grid grid-cols-3 gap-1.5">
                                  <div><Label className="text-[9px] text-gray-500">KG</Label><Input type="number" min="0" step="0.1" value={it.kg} onChange={(e) => updateEditLine(idx, "kg", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`edit-kg-${idx}`} /></div>
                                  <div><Label className="text-[9px] text-gray-500">Sell Rate/kg</Label><Input type="number" min="0" step="0.01" value={it.sell_rate} onChange={(e) => updateEditLine(idx, "sell_rate", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`edit-rate-${idx}`} /></div>
                                  <div><Label className="text-[9px] text-gray-500">Line Total</Label><div className="h-8 px-2 flex items-center bg-indigo-50 rounded border text-xs font-mono font-bold text-indigo-700">{fc(lineBase)}</div></div>
                                </div>
                                <Input value={it.notes} onChange={(e) => updateEditLine(idx, "notes", e.target.value)} placeholder="Notes (opt)" className="h-7 text-[11px] mt-1" />
                              </div>
                            );
                          })
                        )}
                      </div>
                      {editItems.length > 0 && !editItemsLoading && (
                        <div className="bg-emerald-50 px-3 py-2 border-t border-indigo-200 text-[11px] flex items-center justify-between">
                          <span className="font-black text-emerald-800 uppercase">Items Subtotal (Base)</span>
                          <span className="font-mono font-black text-emerald-700">{fc(editItems.reduce((s, it) => s + (parseFloat(it.kg) || 0) * (parseFloat(it.sell_rate) || 0), 0))}</span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-bold text-emerald-700">Total Bill Amount</Label>
                        {editItems.length > 0 ? (() => {
                          const baseFromItems = editItems.reduce((s, it) => s + (parseFloat(it.kg) || 0) * (parseFloat(it.sell_rate) || 0), 0);
                          const gPct = editEntity.data.gst_type === "with_gst" ? parseFloat(editEntity.data.gst_percent || 0) : 0;
                          const grandFromItems = +(baseFromItems + (baseFromItems * gPct / 100)).toFixed(2);
                          return <div className="h-9 px-2 flex items-center bg-emerald-50 border-2 border-emerald-300 rounded text-sm font-mono font-black text-emerald-700" data-testid="edit-total-bill">{fc(grandFromItems)}</div>;
                        })() : (
                          <Input type="number" value={editEntity.data.base_amount ?? editEntity.data.total_bill_amount ?? ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, base_amount: e.target.value } }))} className="h-9 text-sm" data-testid="edit-sale-amount" />
                        )}
                        <p className="text-[10px] text-emerald-600 mt-0.5">{editItems.length > 0 ? "Auto from items + GST" : "Manual (no items)"}</p>
                      </div>
                      <div><Label className="text-xs font-bold text-green-700">Amount Paid</Label><Input type="number" min="0" value={editEntity.data.amount_paid ?? ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, amount_paid: e.target.value } }))} placeholder="0 = fully pending" className="h-9 text-sm border-green-300" data-testid="edit-amount-paid" /></div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 space-y-1.5">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setEditEntity((p) => ({ ...p, data: { ...p.data, gst_type: "without_gst" } }))} className={`h-7 px-2 rounded text-[10px] font-bold flex-1 ${editEntity.data.gst_type !== "with_gst" ? "bg-gray-700 text-white" : "bg-white border border-gray-300 text-gray-600"}`}>Not GST</button>
                        <button type="button" onClick={() => setEditEntity((p) => ({ ...p, data: { ...p.data, gst_type: "with_gst" } }))} className={`h-7 px-2 rounded text-[10px] font-bold flex-1 ${editEntity.data.gst_type === "with_gst" ? "bg-blue-600 text-white" : "bg-white border border-blue-300 text-blue-600"}`}>GST Bill</button>
                      </div>
                      {editEntity.data.gst_type === "with_gst" && (
                        <div className="flex gap-1 flex-wrap">
                          {["0", "5", "12", "18", "28"].map((p) => (
                            <button key={p} type="button" onClick={() => setEditEntity((s) => ({ ...s, data: { ...s.data, gst_percent: parseFloat(p) } }))} className={`h-6 px-2 rounded text-[10px] font-bold ${parseFloat(editEntity.data.gst_percent || 0) === parseFloat(p) ? "bg-blue-600 text-white" : "bg-white border border-blue-200 text-blue-600"}`}>{p}%</button>
                          ))}
                          <Input type="number" placeholder="Custom" value={editEntity.data.gst_percent || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, gst_percent: e.target.value } }))} className="h-6 w-16 text-[10px]" />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {editEntity.type === "expense" && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Bill Number</Label><Input value={editEntity.data.bill_number || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, bill_number: e.target.value } }))} className="h-9 text-sm" data-testid="edit-exp-bill" /></div>
                      <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={editEntity.data.date || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, date: e.target.value } }))} className="h-9 text-sm" /></div>
                    </div>
                    <div><Label className="text-xs font-bold">Description</Label><Input value={editEntity.data.description || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, description: e.target.value } }))} className="h-9 text-sm" data-testid="edit-exp-desc" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Customer/Vendor</Label><Input value={editEntity.data.customer_name || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, customer_name: e.target.value } }))} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs font-bold">Amount</Label><Input type="number" value={editEntity.data.amount || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, amount: e.target.value } }))} className="h-9 text-sm" data-testid="edit-exp-amount" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Category</Label>
                        <Select value={editEntity.data.category || "general"} onValueChange={(v) => setEditEntity((p) => ({ ...p, data: { ...p.data, category: v } }))}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="travel">Travel</SelectItem><SelectItem value="food">Food</SelectItem><SelectItem value="utilities">Utilities</SelectItem><SelectItem value="salary">Salary</SelectItem><SelectItem value="rent">Rent</SelectItem><SelectItem value="supplies">Supplies</SelectItem><SelectItem value="misc">Misc</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs font-bold">Payment Method</Label>
                        <Select value={editEntity.data.payment_method || "cash"} onValueChange={(v) => setEditEntity((p) => ({ ...p, data: { ...p.data, payment_method: v } }))}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
                {editEntity.type === "pending" && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Invoice #</Label><Input value={editEntity.data.invoice_number || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, invoice_number: e.target.value } }))} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={editEntity.data.date || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, date: e.target.value } }))} className="h-9 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs font-bold">Customer</Label><Input value={editEntity.data.customer_name || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, customer_name: e.target.value } }))} className="h-9 text-sm" /></div>
                      <div><Label className="text-xs font-bold">Phone</Label><Input value={editEntity.data.customer_phone || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, customer_phone: e.target.value } }))} className="h-9 text-sm" /></div>
                    </div>
                    <div><Label className="text-xs font-bold">Pending Amount</Label><Input type="number" value={editEntity.data.amount || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, amount: e.target.value } }))} className="h-9 text-sm" /></div>
                    <div><Label className="text-xs font-bold">Remarks</Label><Input value={editEntity.data.remarks || ""} onChange={(e) => setEditEntity((p) => ({ ...p, data: { ...p.data, remarks: e.target.value } }))} className="h-9 text-sm" /></div>
                  </>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 h-9" onClick={() => setEditEntity(null)} data-testid="cancel-edit">Cancel</Button>
                  <Button className="flex-1 h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" onClick={handleSaveEdit} data-testid="save-edit">Save Changes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============== Multi-Product Sale Bill Dialog ============== */}
      <Dialog open={showMultiSale} onOpenChange={setShowMultiSale}>
        <DialogContent className="sm:max-w-[950px] max-h-[92vh] overflow-y-auto" data-testid="ms-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
              <CurrencyInr size={18} className="text-blue-600" weight="bold" /> Multi-Product Sale Bill
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Header */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div><Label className="text-xs font-bold text-blue-700">Customer *</Label><Input value={msInvoice.customer_name} onChange={(e) => setMsInvoice((p) => ({ ...p, customer_name: e.target.value }))} placeholder="Customer name" className="h-9 text-sm" data-testid="ms-acc-customer" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Phone</Label><Input value={msInvoice.customer_contact} onChange={(e) => setMsInvoice((p) => ({ ...p, customer_contact: e.target.value }))} placeholder="Phone" className="h-9 text-sm" data-testid="ms-acc-phone" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Bill #</Label><Input value={msInvoice.bill_number} onChange={(e) => setMsInvoice((p) => ({ ...p, bill_number: e.target.value }))} placeholder="Auto if empty" className="h-9 text-sm" data-testid="ms-acc-bill" /></div>
              <div><Label className="text-xs font-bold text-blue-700">Date *</Label><Input type="date" value={msInvoice.date} onChange={(e) => setMsInvoice((p) => ({ ...p, date: e.target.value }))} className="h-9 text-sm" data-testid="ms-acc-date" /></div>
            </div>

            {/* Line items */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 flex items-center justify-between">
                <h4 className="text-xs font-black text-gray-700 uppercase">Sale Items ({msItems.length})</h4>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={addMsLine} data-testid="ms-acc-add-line">
                  <Plus size={12} className="mr-0.5" /> Add Item
                </Button>
              </div>
              <div className="divide-y divide-gray-100 max-h-[380px] overflow-y-auto">
                {msItems.map((it, idx) => {
                  const kgN = parseFloat(it.kg) || 0;
                  const rateN = parseFloat(it.sell_rate) || 0;
                  const lineBase = +(kgN * rateN).toFixed(2);
                  const availBatches = batches.filter(b => b.product_name === it.product_name && b.kg_remaining > 0);
                  const selectedBatch = availBatches.find(b => b.batch_code === it.batch_code);
                  const lineBuy = selectedBatch ? kgN * (selectedBatch.kilo_price || 0) : 0;
                  const lineProfit = lineBase - lineBuy;
                  return (
                    <div key={idx} className="p-2 bg-white hover:bg-gray-50" data-testid={`ms-acc-line-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-black flex items-center justify-center">{idx + 1}</span>
                        <select value={it.product_name} onChange={(e) => { updateMsLine(idx, "product_name", e.target.value); updateMsLine(idx, "batch_code", ""); }} className="h-8 text-xs border rounded flex-1 bg-white px-2" data-testid={`ms-acc-prod-${idx}`}>
                          <option value="">Select Product</option>
                          {[...new Set(batches.filter(b => b.kg_remaining > 0).map(b => b.product_name))].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <select value={it.batch_code} onChange={(e) => updateMsLine(idx, "batch_code", e.target.value)} className="h-8 text-xs border rounded w-48 bg-white px-2" data-testid={`ms-acc-batch-${idx}`}>
                          <option value="fifo">Auto FIFO</option>
                          {availBatches.map((b) => <option key={b.batch_code} value={b.batch_code}>{b.batch_code} · {b.kg_remaining}kg @ ₹{b.kilo_price}</option>)}
                        </select>
                        <button onClick={() => removeMsLine(idx)} disabled={msItems.length === 1} className="h-7 w-7 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-30 flex items-center justify-center" data-testid={`ms-acc-remove-${idx}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                        <div><Label className="text-[9px] text-gray-500">KG</Label><Input type="number" min="0" step="0.1" value={it.kg} onChange={(e) => updateMsLine(idx, "kg", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`ms-acc-kg-${idx}`} /></div>
                        <div><Label className="text-[9px] text-gray-500">Sell Rate/kg</Label><Input type="number" min="0" step="0.01" value={it.sell_rate} onChange={(e) => updateMsLine(idx, "sell_rate", e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`ms-acc-rate-${idx}`} /></div>
                        <div><Label className="text-[9px] text-gray-500">Line Total</Label><div className="h-8 px-2 flex items-center bg-blue-50 rounded border text-xs font-mono font-bold text-blue-700">{fc(lineBase)}</div></div>
                        <div><Label className="text-[9px] text-gray-500">Buy Cost</Label><div className="h-8 px-2 flex items-center bg-gray-50 rounded border text-xs font-mono text-gray-700">{selectedBatch ? fc(lineBuy) : "FIFO"}</div></div>
                        <div><Label className="text-[9px] text-gray-500">Est. Profit</Label><div className={`h-8 px-2 flex items-center rounded border text-xs font-mono font-black ${lineProfit >= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{selectedBatch ? fc(lineProfit) : "—"}</div></div>
                      </div>
                      <Input value={it.notes} onChange={(e) => updateMsLine(idx, "notes", e.target.value)} placeholder="Notes (opt)" className="h-7 text-[11px] mt-1.5" />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GST + payment + pending due date */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 bg-gray-50 p-3 rounded-lg border">
              <div>
                <Label className="text-xs font-bold text-gray-700">GST Bill?</Label>
                <div className="flex gap-1 mt-1">
                  <button type="button" onClick={() => setMsInvoice((p) => ({ ...p, gst_type: "without_gst" }))} className={`h-8 px-2 rounded text-[10px] font-bold flex-1 ${msInvoice.gst_type !== "with_gst" ? "bg-gray-700 text-white" : "bg-white border border-gray-300 text-gray-600"}`} data-testid="ms-acc-no-gst">Not GST</button>
                  <button type="button" onClick={() => setMsInvoice((p) => ({ ...p, gst_type: "with_gst" }))} className={`h-8 px-2 rounded text-[10px] font-bold flex-1 ${msInvoice.gst_type === "with_gst" ? "bg-blue-600 text-white" : "bg-white border border-blue-300 text-blue-600"}`} data-testid="ms-acc-with-gst">GST Bill</button>
                </div>
                {msInvoice.gst_type === "with_gst" && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {["0", "5", "12", "18", "28"].map((p) => (
                      <button key={p} type="button" onClick={() => setMsInvoice((s) => ({ ...s, gst_percent: p }))} className={`h-6 px-2 rounded text-[10px] font-bold ${msInvoice.gst_percent === p ? "bg-blue-600 text-white" : "bg-white border border-blue-200 text-blue-600"}`}>{p}%</button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold text-emerald-700">Total Bill Amount</Label>
                <div className="h-9 px-2 flex items-center bg-emerald-50 border-2 border-emerald-300 rounded text-sm font-mono font-black text-emerald-700" data-testid="ms-acc-total-bill">{fc(msGrand)}</div>
                <p className="text-[10px] text-emerald-600 mt-0.5">Auto from items + GST</p>
              </div>
              <div>
                <Label className="text-xs font-bold text-green-700">Amount Paid</Label>
                <Input type="number" min="0" value={msInvoice.amount_paid} onChange={(e) => setMsInvoice((p) => ({ ...p, amount_paid: e.target.value }))} placeholder="0 = fully pending" className="h-9 text-sm border-green-300 focus:border-green-500" data-testid="ms-acc-advance" />
                <p className="text-[10px] text-green-600 mt-0.5">Leave empty = fully paid</p>
              </div>
              <div>
                <Label className="text-xs font-bold text-orange-700">Pending Due Date</Label>
                <Input type="date" value={msInvoice.due_date} onChange={(e) => setMsInvoice((p) => ({ ...p, due_date: e.target.value }))} className="h-9 text-sm border-orange-200" data-testid="ms-acc-due" />
                <p className="text-[10px] text-orange-600 mt-0.5">When balance expected</p>
              </div>
              <div>
                <Label className="text-xs font-bold text-gray-700">Payment Method</Label>
                <select value={msInvoice.payment_method} onChange={(e) => setMsInvoice((p) => ({ ...p, payment_method: e.target.value }))} className="h-9 text-sm border rounded w-full bg-white px-2" data-testid="ms-acc-method">
                  <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="bank">Bank</option><option value="cheque">Cheque</option>
                </select>
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="ms-acc-totals">
              <Card className="p-2.5 border-2 border-gray-200 bg-gray-50"><p className="text-[10px] font-bold text-gray-700 uppercase">Base Total</p><p className="text-base font-black text-gray-900 font-mono">{fc(msBase)}</p></Card>
              <Card className="p-2.5 border-2 border-blue-200 bg-blue-50"><p className="text-[10px] font-bold text-blue-700 uppercase">GST ({msGstPct}%)</p><p className="text-base font-black text-blue-700 font-mono">{fc(msGstAmt)}</p></Card>
              <Card className="p-2.5 border-2 border-emerald-300 bg-emerald-50"><p className="text-[10px] font-bold text-emerald-700 uppercase">Grand Total</p><p className="text-base font-black text-emerald-700 font-mono">{fc(msGrand)}</p></Card>
              <Card className="p-2.5 border-2 border-green-300 bg-green-50"><p className="text-[10px] font-bold text-green-700 uppercase">Amount Paid</p><p className="text-base font-black text-green-700 font-mono">{fc(msAdvance)}</p></Card>
              <Card className={`p-2.5 border-2 ${msPendingDue > 0 ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                <p className={`text-[10px] font-bold uppercase ${msPendingDue > 0 ? "text-red-700" : "text-gray-500"}`}>Pending Due</p>
                <p className={`text-base font-black font-mono ${msPendingDue > 0 ? "text-red-700" : "text-gray-500"}`}>{fc(msPendingDue)}</p>
                {msPendingDue > 0 && msInvoice.due_date && <p className="text-[10px] text-red-600 font-semibold">by {msInvoice.due_date}</p>}
              </Card>
            </div>

            <Button className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold" onClick={submitMultiSale} data-testid="ms-acc-save">
              <CheckCircle size={16} className="mr-1" weight="bold" /> Save Sale Bill{msPendingDue > 0 ? ` + Track Pending ${fc(msPendingDue)}` : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== Delete Confirm Dialog ============== */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="delete-confirm-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-red-600" style={{ fontFamily: "'Gupter', serif" }}>Delete this {confirmDelete?.type}?</DialogTitle></DialogHeader>
          {confirmDelete && (
            <div className="space-y-3 py-2">
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                <p className="font-bold">{confirmDelete.data.bill_number || confirmDelete.data.invoice_number || confirmDelete.data.description || "Entry"}</p>
                <p className="text-gray-500 text-xs">{confirmDelete.data.date} · {fc(confirmDelete.data.amount || confirmDelete.data.total_bill_amount || 0)}</p>
              </div>
              <p className="text-xs text-red-600">This action cannot be undone.</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-9" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete">Cancel</Button>
                <Button className="flex-1 h-9 bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete} data-testid="confirm-delete"><Trash size={12} className="mr-1" /> Delete</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
